/**
 * ============================================================================
 * CENTRALIZED ROUTE & COMPONENT CONFIGURATION
 * ============================================================================
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Route paths
 * - Components
 * - Permissions
 * - Route access control
 *
 * Define a route ONCE here, use it EVERYWHERE automatically!
 * ============================================================================
 */

import { lazy, ComponentType } from 'react';
import { UserRole } from './roles';

// Import regular components that are not lazy loaded
import Login from '@pages/Auth/Login/Login';
import RequestOTP from '@pages/Auth/ForgotPassword/ForgotPassword';
import VerifyOTP from '@pages/Auth/VerifyOTP/VerifyOTP';
import CreateNewPassword from '@pages/Auth/CreateNewPassword/CreateNewPassword';
import MainLayout from '@/PageLayout/PublicLayout/MainLayout';
import LandingPage from '@pages/Public/Landing/LandingPage';
import PublicFeedbackForm from '@pages/Public/FeedbackForm/PublicFeedbackForm';
import CatalogManagement from '@pages/Admin/Catelog/CatalogManagement';
import MyTeam from '@pages/Admin/MyTeam/MyTeam';
import Users from '@pages/Admin/Users/Users';
import ReportsAnalytics from '@pages/Admin/Report/ReportAnalytics';
import ILTManagement from '@pages/Admin/ILTResourseManagement/ILTResourseManagement';
import CourseManagement from '@pages/Admin/CourseManagement/CourseManagement';
import MarketingAndNotification from '@pages/Admin/MarketingAndNotification/MarketingAndNotification';
import BulkUploadPage from '@pages/Admin/BulkUploader/BulkUploadPage';
import LearningAssignmentTool from '@pages/Admin/LearningAssignmentTool/LearningAssignmentTool';
import TrainerDashboard from '@/pages/Trainer/Dashboard/TrainerDashboard';
import TrainerBrowseCourse from '@pages/Trainer/MyLearnings/BrowseCourses/BrowseCourses';

import TrainerMyCourses from '@pages/Trainer/MyLearnings/MyCourses/MyCourses';
import TrainerWishlist from '@pages/Trainer/MyLearnings/Wishlist/Wishlist';
import TrainerViewCoursePage from '@pages/Trainer/MyLearnings/ViewCourse/ViewCourse';
import TrainerDashboardPage from '@/pages/Trainer/Dashboard/TrainerDashboard';
import TrainerCourseManagement from '@pages/Trainer/CourseManagement/CourseManagement';
import TrainerAssessmentManagement from '@pages/Trainer/AssessmentManagement/AssessmentManagement';
import TrainerGroupProjectManagement from '@pages/Trainer/GroupProjectManagement/GroupProjectManagement';
import TrainerMyTeam from '@pages/Trainer/MyTeam/MyTeam';
import TrainerUsers from '@pages/Trainer/Users/Users';
import TrainerLearningAssignmentTool from '@pages/Trainer/LearningAssignmentTool/LearningAssignmentTool';
import TrainerReportsAnalytics from '@pages/Trainer/Report/ReportAnalytics';
import TrainerMarketingAndNotification from '@pages/Trainer/MarketingAndNotification/MarketingAndNotification';
import TrainerILTManagement from '@pages/Trainer/ILTResourseManagement/ILTResourseManagement';
import TrainerAccountSettings from '@pages/Learner/AccountSettings/AccountSettings';
import AccountSettingsRouter from './AccountSettingsRouter/AccountSettingsRouter';
import SupportRouter from './SupportRouter/SupportRouter';
import SuperAdminDashboard from '@/pages/SuperAdmin/Dashboard/SuperAdminDashboard';

// Common Support Pages
import SupportAndHelpPage from '@pages/Common/SupportAndHelp/SupportAndHelpPage';
import ChatWithUs from '@pages/Common/SupportAndHelp/ChatWithUs';
import CallUs from '@pages/Common/SupportAndHelp/CallUs';
import WriteYourQuery from '@pages/Common/SupportAndHelp/WriteYourQuery';
import SuperAdminContentManagement from '@pages/SuperAdmin/ContentManagement/ContentManagement';
import SuperAdminCourseManagement from '@/pages/SuperAdmin/CourseManagement/SuperAdminCourseManagement';
import SuperAdminAssessmentManagement from '@pages/SuperAdmin/AssessmentManagement/AssessmentManagement';
import SuperAdminGroupProjectManagement from '@pages/SuperAdmin/GroupProjectManagement/GroupProjectManagement';
import SuperAdminCertificationManagement from '@pages/SuperAdmin/CertificationManagement/CertificationManagement';
import SuperAdminCatalogManagement from '@/pages/SuperAdmin/Catelog/SuperAdminCatalogManagement';
import SuperAdminMyTeam from '@/pages/SuperAdmin/MyTeam/SuperAdminMyTeam';
import SuperAdminUsers from '@/pages/SuperAdmin/Users/SuperAdminUsers';
import SuperAdminLearningAssignmentTool from '@/pages/SuperAdmin/LearningAssignmentTool/SuperAdminLearningAssignmentTool';
import SuperAdminBulkUploadPage from '@/pages/SuperAdmin/BulkUploader/SuperAdminBulkUploadPage';
import SuperAdminFeedbackManagement from '@pages/SuperAdmin/FeedbackManagement/SuperAdminFeedbackManagement';
import SuperAdminMarketingAndNotification from '@pages/SuperAdmin/MarketingAndNotification/MarketingAndNotification';
import SuperAdminILTResourceManagement from '@pages/SuperAdmin/ILTResourseManagement/ILTResourseManagement';
import SuperAdminReportsAnalytics from '@pages/SuperAdmin/Report/ReportAnalytics';


/**
 * Route Configuration Interface
 * Defines route, component, and permissions
 */
export interface RouteConfig {
  path: string;
  component: ComponentType<any>;
  allowedRoles?: UserRole[];
  isPublic?: boolean;
}

/**
 * ============================================================================
 * LAZY-LOADED COMPONENTS
 * ============================================================================
 */

// Public Pages
const FeaturesPage = lazy(() => import('@pages/Public/Features/FeaturesPage'));
const PricingPage = lazy(() => import('@pages/Public/Pricing/PricingPage'));
const AboutPage = lazy(() => import('@pages/Public/About/AboutPage'));
const ContactPage = lazy(() => import('@pages/Public/Contact/ContactPage'));

// Learner/Trainee Pages
const LoggedInHome = lazy(() => import('@pages/Learner/Home/Home'));
const AILearningPaths = lazy(() => import('@pages/Learner/AiLearningPath/AILearningPath'));
const LoggedInTranscript = lazy(() => import('@pages/Learner/Transcript/Transcript'));
const BrowseCourses = lazy(() => import('@/pages/Learner/MyLearnings/BrowseCourses/BrowseCourses'));
const MyCourses = lazy(() => import('@/pages/Learner/MyLearnings/MyCourses/MyCourses'));
const Wishlist = lazy(() => import('@/pages/Learner/MyLearnings/Wishlist/Wishlist'));
const LoggedInAchievements = lazy(() => import('@pages/Learner/Achievements/Achievements'));
const CertificationsPage = lazy(() => import('@pages/Learner/Certifications/CertificationPage/CertificationPage'));
const UploadCertification = lazy(() => import('@pages/Learner/Certifications/UploadCertification/UploadCertification'));
const ViewCoursePage = lazy(() => import('@/pages/Learner/MyLearnings/ViewCourse/ViewCourse'));
const LessonViewer = lazy(() => import('@/pages/Learner/MyLearnings/LessonViewer/LessonViewer'));
const ComingSoon = lazy(() => import('@pages/Learner/ComingSoon/ComingSoon'));

const CommonAccountSettings = lazy(() => import('@pages/Learner/AccountSettings/AccountSettings'));

// Admin Pages
const AdminDashboard = lazy(() => import('@pages/Admin/Dashboard/AdminDashboard'));
const AdminComingSoon = lazy(() => import('@pages/Admin/ComingSoon/AdminComingSoon'));
const ContentManagement = lazy(() => import('@pages/Admin/ContentManagement/ContentManagement'));
const AdminSettings = lazy(() => import('@pages/Admin/Settings/AdminSettings'));
const CertificationManagement = lazy(() => import('@pages/Admin/CertificationManagement/CertificationManagement'));
const FeedbackManagement = lazy(() => import('@pages/Admin/FeedbackManagement/FeedbackManagement'));
const AssessmentManagement = lazy(() => import('@pages/Admin/AssessmentManagement/AssessmentManagement'));
const GroupProjectManagement = lazy(() => import('@pages/Admin/GroupProjectManagement/GroupProjectManagement'));


/**
 * ============================================================================
 * ALL ROUTES CONFIGURATION
 * ============================================================================
 * Define your route ONCE with all its properties.
 * This configuration is used for:
 * - App.tsx routing
 * - Permission checking
 * - Navigation
 */
export const ROUTES: RouteConfig[] = [
  // ========================================================================
  // PUBLIC ROUTES
  // ========================================================================
  {
    path: '/',
    component: LandingPage,
    isPublic: true,
  },
  {
    path: '/landing',
    component: LandingPage,
    isPublic: true,
  },
  {
    path: '/features',
    component: FeaturesPage,
    isPublic: true,
  },
  {
    path: '/pricing',
    component: PricingPage,
    isPublic: true,
  },
  {
    path: '/about',
    component: AboutPage,
    isPublic: true,
  },
  {
    path: '/contact',
    component: ContactPage,
    isPublic: true,
  },
  {
    path: '/login',
    component: Login,
    isPublic: true,
  },
  {
    path: '/forgot-password',
    component: RequestOTP,
    isPublic: true,
  },
  {
    path: '/verify-otp',
    component: VerifyOTP,
    isPublic: true,
  },
  {
    path: '/create-new-password',
    component: CreateNewPassword,
    isPublic: true,
  },
  {
    path: '/feedback/:slug',
    component: PublicFeedbackForm,
    isPublic: true,
  },

  // ========================================================================
  // SHARED ROUTES (Role-based redirects)
  // ========================================================================

  // Unified account settings route that redirects based on user role
  {
    path: '/account-settings',
    component: AccountSettingsRouter,
    allowedRoles: [UserRole.TRAINEE, UserRole.ADMIN, UserRole.TRAINER, UserRole.SUPER_ADMIN],
  },

  // Unified support route that redirects based on user role
  {
    path: '/support',
    component: SupportRouter,
    allowedRoles: [UserRole.TRAINEE, UserRole.ADMIN, UserRole.TRAINER, UserRole.SUPER_ADMIN],
  },

  // ========================================================================
  // TRAINEE ROUTES (/trainee/*)
  // ========================================================================

  // Trainee Home
  {
    path: '/trainee/home',
    component: LoggedInHome,
    allowedRoles: [UserRole.TRAINEE],
  },

  // My Learnings
  {
    path: '/trainee/my-learnings/browse-courses',
    component: BrowseCourses,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/my-learnings/my-course',
    component: MyCourses,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/my-learnings/wishlist',
    component: Wishlist,
    allowedRoles: [UserRole.TRAINEE],
  },

  // Learning Content
  {
    path: '/trainee/achievements',
    component: LoggedInAchievements,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/certifications',
    component: CertificationsPage,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/upload-certification',
    component: UploadCertification,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/transcript',
    component: LoggedInTranscript,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/ai-paths',
    component: AILearningPaths,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/course/:courseId',
    component: ViewCoursePage,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/course/:courseId/lesson/:lessonId',
    component: LessonViewer,
    allowedRoles: [UserRole.TRAINEE],
  },

  // Trainee Coming Soon Features
  {
    path: '/trainee/lingo-lab',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/assessment',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/proctor',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/performance',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/team',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/group-projects',
    component: ComingSoon,
    allowedRoles: [UserRole.TRAINEE],
  },

  {
    path: '/trainee/account-settings',
    component: CommonAccountSettings,
    allowedRoles: [UserRole.TRAINEE],
  },

  // Trainee Support & Help
  {
    path: '/trainee/support',
    component: SupportAndHelpPage,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/support/chat',
    component: ChatWithUs,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/support/call',
    component: CallUs,
    allowedRoles: [UserRole.TRAINEE],
  },
  {
    path: '/trainee/support/query',
    component: WriteYourQuery,
    allowedRoles: [UserRole.TRAINEE],
  },

  // ========================================================================
  // ADMIN ROUTES (/admin/*)
  // ========================================================================

  // Admin Dashboard
  {
    path: '/admin/dashboard',
    component: AdminDashboard,
    allowedRoles: [UserRole.ADMIN],
  },

  // Content Management
  {
    path: '/admin/content/architect',
    component: AdminComingSoon,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/management',
    component: ContentManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/courses',
    component: CourseManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/catalog',
    component: CatalogManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/assessments',
    component: AssessmentManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/group-projects',
    component: GroupProjectManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/content/certifications',
    component: CertificationManagement,
    allowedRoles: [UserRole.ADMIN],
  },

  // User Management
  {
    path: '/admin/users/team',
    component: MyTeam,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/users/list',
    component: Users,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/users/assignments',
    component: AdminComingSoon,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/users/learning-assignment',
    component: LearningAssignmentTool,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/users/performance',
    component: AdminComingSoon,
    allowedRoles: [UserRole.ADMIN],
  },

  // Reports
  {
    path: '/admin/reports',
    component: ReportsAnalytics,
    allowedRoles: [UserRole.ADMIN],
  },

  // Tools
  {
    path: '/admin/tools/bulk-uploader',
    component: BulkUploadPage,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/tools/feedback',
    component: FeedbackManagement,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/tools/marketing',
    component: MarketingAndNotification,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/tools/ilt-resources',
    component: ILTManagement,
    allowedRoles: [UserRole.ADMIN],
  },

  // Admin Settings & Support
  {
    path: '/admin/settings',
    component: AdminSettings,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/account-settings',
    component: CommonAccountSettings,
    allowedRoles: [UserRole.ADMIN],
  },

  // Admin Support & Help
  {
    path: '/admin/support',
    component: SupportAndHelpPage,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/support/chat',
    component: ChatWithUs,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/support/call',
    component: CallUs,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/admin/support/query',
    component: WriteYourQuery,
    allowedRoles: [UserRole.ADMIN],
  },

  // ========================================================================
  // TRAINER ROUTES (/trainer/*)
  // ========================================================================

  // Trainer Dashboard
  {
    path: '/trainer/dashboard',
    component: TrainerDashboard,
    allowedRoles: [UserRole.TRAINER],
  },

  // My Learnings
  {
    path: '/trainer/my-learnings/browse-courses',
    component: TrainerBrowseCourse,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/my-learnings/my-courses',
    component: TrainerMyCourses,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/my-learnings/wishlist',
    component: TrainerWishlist,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/course/:courseId',
    component: TrainerViewCoursePage,
    allowedRoles: [UserRole.TRAINER],
  },

  // Content Management
  {
    path: '/trainer/content/course-management',
    component: TrainerCourseManagement,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/content/assessments',
    component: TrainerAssessmentManagement,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/content/group-projects',
    component: TrainerGroupProjectManagement,
    allowedRoles: [UserRole.TRAINER],
  },

  // User Management
  {
    path: '/trainer/users/my-team',
    component: TrainerMyTeam,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/users/user-list',
    component: TrainerUsers,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/users/learning-assignment',
    component: TrainerLearningAssignmentTool,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/users/performance',
    component:AdminComingSoon,
    allowedRoles: [UserRole.TRAINER],
  },

  // Reports
  {
    path: '/trainer/reports',
    component: TrainerReportsAnalytics,
    allowedRoles: [UserRole.TRAINER],
  },

  // Tools
  {
    path: '/trainer/tools/marketing',
    component: TrainerMarketingAndNotification,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/tools/ilt-resources',
    component: TrainerILTManagement,
    allowedRoles: [UserRole.TRAINER],
  },

  // Trainer Support
  {
    path: '/trainer/account-settings',
    component: CommonAccountSettings,
    allowedRoles: [UserRole.TRAINER],
  },

  // Trainer Support & Help
  {
    path: '/trainer/support',
    component: SupportAndHelpPage,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/support/chat',
    component: ChatWithUs,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/support/call',
    component: CallUs,
    allowedRoles: [UserRole.TRAINER],
  },
  {
    path: '/trainer/support/query',
    component: WriteYourQuery,
    allowedRoles: [UserRole.TRAINER],
  },

  // ========================================================================
  // SUPER ADMIN ROUTES (/superadmin/*)
  // ========================================================================

  // Super Admin Dashboard
  {
    path: '/superadmin/dashboard',
    component: SuperAdminDashboard,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // Content Management
  {
    path: '/superadmin/content/content-architect',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/content-management',
    component: SuperAdminContentManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/course-management',
    component: SuperAdminCourseManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/catalog',
    component: SuperAdminCatalogManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/assessments',
    component: SuperAdminAssessmentManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/group-projects',
    component: SuperAdminGroupProjectManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/certifications',
    component: SuperAdminCertificationManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/content/training-assistant',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // User Management
  {
    path: '/superadmin/users/my-team',
    component: SuperAdminMyTeam,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/user-list',
    component: SuperAdminUsers,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/org-hierarchy',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/performance-tracking',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/dynamic-assessment',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/learning-assignment',
    component: SuperAdminLearningAssignmentTool,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/users/security',
    component: AdminComingSoon,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // Tools
  {
    path: '/superadmin/tools/bulk-uploader',
    component: SuperAdminBulkUploadPage,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/tools/feedback',
    component: SuperAdminFeedbackManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/tools/marketing',
    component: SuperAdminMarketingAndNotification,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/tools/ilt-resources',
    component: SuperAdminILTResourceManagement,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // Reports
  {
    path: '/superadmin/reports',
    component: SuperAdminReportsAnalytics,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // Super Admin Settings & Support
  {
    path: '/superadmin/settings',
    component: AdminSettings,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/account-settings',
    component: CommonAccountSettings,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },

  // Super Admin Support & Help
  {
    path: '/superadmin/support',
    component: SupportAndHelpPage,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/support/chat',
    component: ChatWithUs,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/support/call',
    component: CallUs,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
  {
    path: '/superadmin/support/query',
    component: WriteYourQuery,
    allowedRoles: [UserRole.SUPER_ADMIN],
  },
];

/**
 * ============================================================================
 * HELPER FUNCTIONS
 * ============================================================================
 */

/**
 * Get all routes accessible to a user role
 */
export const getRoutesForRole = (userRole: UserRole | undefined): RouteConfig[] => {
  if (!userRole) return [];
  return ROUTES.filter((route) => {
    if (route.isPublic) return true;
    if (!route.allowedRoles) return false;
    return route.allowedRoles.includes(userRole);
  });
};

/**
 * Check if user has permission to access a route
 */
export const hasRoutePermission = (userRole: UserRole | undefined, routePath: string): boolean => {
  if (!userRole) return false;

  // Find exact or dynamic route match
  const route = ROUTES.find((r) => {
    // Public routes are always accessible
    if (r.isPublic) return false;

    // Exact match
    if (r.path === routePath) return true;

    // Dynamic route match (e.g., /trainee/course/:courseId)
    const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(routePath);
  });

  if (!route) {
    console.log(`🚫 Route "${routePath}" not found in config`);
    return false;
  }

  const hasPermission = route.allowedRoles?.includes(userRole) ?? false;
  console.log(
    `${hasPermission ? '✅' : '🚫'} Role "${userRole}" ${
      hasPermission ? 'CAN' : 'CANNOT'
    } access "${routePath}"`
  );

  return hasPermission;
};

/**
 * Get a specific route by path
 */
export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return ROUTES.find((r) => {
    // Exact match
    if (r.path === path) return true;

    // Dynamic route match
    const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(path);
  });
};
